import * as React from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Armchair,
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  FileText,
  FileUp,
  Filter,
  LayoutDashboard,
  LayoutGrid,
  Map as MapIcon,
  Radio,
  Settings,
  UserCog,
  ShieldCheck,
  MoreHorizontal,
  LogOut,
  UserCheck,
} from "lucide-react";
import {
  EduViaOSMark,
  Sidebar,
  SidebarHeader,
  SidebarBrand,
  SidebarBody,
  SidebarGroup,
  SidebarItem,
  SidebarFooter,
  SidebarUser,
  Topbar,
  SidebarToggle,
  TopbarSearch,
  TopbarSpacer,
  TopbarActions,
  ThemeCustomizer,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  MobileBottomNav,
  MobileBottomNavItem,
  MobileBottomNavMore,
  MobileBottomNavSheetItem,
  MobileBottomNavSheetClose,
  useAppearance,
  cn,
} from "@eduviaos/ui-kit";
import { useAuth } from "@/auth/AuthProvider";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useCatalogueReadiness, readinessGateMeta } from "@/hooks/useCatalogueReadiness";
import { useTenantBrandingContext } from "@/components/useTenantBrandingContext";
import { MODULE_CATALOG, navModules, type ModuleDescriptor } from "@/lib/moduleCatalog";
import {
  getVerticalProfile,
  hasAcademicCatalogue,
  hasAdmissionAssessments,
  importPeopleLabelFor,
  moduleNavLabelFor,
  type VerticalProfile,
} from "@/lib/verticalProfile";
import {
  applyAdmissionsEditorNavEntry,
  applyFormsNavEntry,
  applyImportStudentsNavEntry,
  applySeatConfigNavEntry,
  applySeatZonesNavEntry,
  applySeatSeatsNavEntry,
  applySeatAllocationsNavEntry,
  applySeatBookingsNavEntry,
  applySeatFloorNavEntries,
} from "@/components/appShellNav";
import { useCanEditForms } from "@/hooks/useFormsAuthoring";
import { useCanEditAdmissions } from "@/hooks/useAdmissionsAuthoring";
import { useCanEditSeating, useCanManageBookings } from "@/hooks/useSeatingAuthoring";

/**
 * Phase D0c — Institution-admin AppShell.
 *
 * Module-gated nav: the sidebar's primary group is computed from the
 * tenant's `enabled_modules` (`/me/dashboard/summary`) so a coaching
 * tenant doesn't see Hostel / Transport, and a school tenant doesn't
 * see hypothetical SC-only modules. Modules whose BE data plane
 * hasn't shipped (`empty_states === "not_implemented"`) get a
 * "Coming soon" pill in the nav so the principal knows the link
 * leads to a placeholder.
 *
 * Loading state: while the dashboard summary is fetching, we render
 * the nav with NO module entries (only Dashboard + Settings) to avoid
 * flashing modules that may not be enabled. The flash window is
 * <100ms in dev with Mongo localhost; on stage with cached caps it's
 * indistinguishable from instant.
 */

interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
  /**
   * Phase H4 FE-2 — when set, the nav entry renders with a muted
   * "prerequisites needed" decoration and a tooltip listing the
   * missing[] gate codes. Click-through is still allowed — the
   * `<PrerequisiteGate>` on the route renders the configure-
   * prerequisites landing so the admin can fix the missing pieces.
   */
  prerequisiteHint?: {
    missing: string[];
    counts: Record<string, number>;
  };
}

const FIXED_NAV: NavEntry[] = [{ to: "/dashboard", label: "Dashboard", icon: <LayoutDashboard /> }];

// Phase H4 FE-1 — the Catalogue is foundational tenant configuration
// (subjects + sections + coverage matrix) that the Timetable,
// Attendance, and Substitution modules all depend on. It lives in the
// "Other" sidebar group next to Settings + Security so it's always
// discoverable, regardless of which modules a tenant has enabled.
const SETTINGS_NAV: NavEntry[] = [
  { to: "/catalogue", label: "Catalogue", icon: <BookOpen /> },
  { to: "/settings", label: "Settings", icon: <Settings /> },
  { to: "/security", label: "Security", icon: <ShieldCheck /> },
];

// Phase H6d — the standing-importer entry. Rendered between Catalogue
// and Settings when the current user holds `import_students` so the
// admin can re-import students after first-run without re-entering the
// wizard. Hidden for users who can't act on it (staff without an
// override, parents, students) per the same nav-hygiene rule the
// rest of this shell follows.
const IMPORT_STUDENTS_NAV_ENTRY: NavEntry = {
  to: "/imports/students",
  label: "Import students",
  icon: <FileUp />,
};

// Phase F-finale-5 Slice 5a — the tenant-self forms editor entry.
// Rendered in the settings group (after Catalogue) only when the
// principal holds `manage_forms` AND the SA hasn't paused
// `forms_editable_by_tenant` (resolved via `useCanEditForms`).
const FORMS_NAV_ENTRY: NavEntry = {
  to: "/forms",
  label: "Forms",
  icon: <FileText />,
};

// Phase F-finale-6 Slice 6b — the tenant-self admission-funnel editor
// entry. Rendered in the settings group (after Catalogue) only when the
// principal holds `manage_admissions` AND the SA hasn't paused
// `admissions_editable_by_tenant` (resolved via `useCanEditAdmissions`).
const ADMISSIONS_EDITOR_NAV_ENTRY: NavEntry = {
  to: "/admissions/funnel",
  label: "Admission funnel",
  icon: <Filter />,
};

// Phase F-finale-7 Slice 7b.1 — the tenant-self seat-assignment-config
// editor entry. Rendered in the settings group (after Catalogue) only when
// the principal holds `manage_zones` (resolved via `useCanEditSeating`) AND
// the tenant is a `study_centre` — seat & space management is a
// study-centre-only feature.
const SEAT_CONFIG_NAV_ENTRY: NavEntry = {
  to: "/seating/config",
  label: "Seat assignment",
  icon: <Armchair />,
};

// Phase F-finale-7 Slice 7b.4a — the seat-management "Zones" entry. Gated
// identically to the seat-config entry (`manage_zones` AND a `study_centre`
// tenant) and spliced directly after it so the seat-management surfaces
// (config → zones → seats/bookings as later slices land) cluster together.
const SEAT_ZONES_NAV_ENTRY: NavEntry = {
  to: "/seating/zones",
  label: "Zones",
  icon: <LayoutGrid />,
};

// Slice 7b.4b — the seat-inventory surface, spliced innermost so the
// seat-management cluster reads config → zones → seats. Reuses the
// Armchair glyph family (config uses the filled chair) with a distinct
// grid-of-seats affordance would over-index on iconography, so the plain
// Armchair keeps the cluster visually cohesive.
const SEAT_SEATS_NAV_ENTRY: NavEntry = {
  to: "/seating/seats",
  label: "Seats",
  icon: <Armchair />,
};

// Slice 7b.4c — the fixed-mode permanent-allocation surface, spliced
// innermost so the seat-management cluster reads config → zones → seats →
// allocations. `UserCheck` (a member with a tick) distinguishes the
// people-facing allocation view from the seat-inventory (Armchair) and
// zone-layout (LayoutGrid) surfaces.
const SEAT_ALLOCATIONS_NAV_ENTRY: NavEntry = {
  to: "/seating/allocations",
  label: "Allocations",
  icon: <UserCheck />,
};

// Slice 7b.4d — the dynamic/zone-only per-session booking admin surface,
// spliced innermost so the seat-management cluster reads config → zones →
// seats → allocations → bookings. `CalendarClock` reads as a time-boxed,
// per-session reservation, distinct from the permanent-allocation UserCheck
// and the seat-inventory Armchair. Gated on `manage_bookings` (the booking
// read/write guard) rather than `manage_zones` — see `useCanManageBookings`.
const SEAT_BOOKINGS_NAV_ENTRY: NavEntry = {
  to: "/seating/bookings",
  label: "Bookings",
  icon: <CalendarClock />,
};

// Phase SC-22c — floor plan editor, per-learner seat plans and the live
// floor. Spliced after Bookings by `applySeatFloorNavEntries`.
const SEAT_FLOOR_NAV_ENTRIES: Record<"floorPlan" | "seatPlans" | "liveFloor", NavEntry> = {
  floorPlan: { to: "/seating/floor-plan", label: "Floor plan", icon: <MapIcon /> },
  seatPlans: { to: "/seating/plans", label: "Seat plans", icon: <UserCog /> },
  liveFloor: { to: "/seating/live", label: "Live floor", icon: <Radio /> },
};

function NavSidebarItem({ to, label, icon, prerequisiteHint }: NavEntry) {
  const item = (
    <NavLink to={to} end className="contents">
      {({ isActive }) => (
        <SidebarItem
          icon={
            prerequisiteHint ? (
              <span className="relative inline-flex">
                {icon}
                <AlertTriangle
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 size-3 fill-amber-500 text-amber-700 dark:text-amber-300"
                />
              </span>
            ) : (
              icon
            )
          }
          label={label}
          active={isActive}
          className={cn(prerequisiteHint ? "text-muted-foreground" : undefined)}
        />
      )}
    </NavLink>
  );

  if (!prerequisiteHint) return item;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent side="right" align="start" className="max-w-xs">
        <p className="text-xs font-medium">Configure prerequisites</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] leading-snug">
          {prerequisiteHint.missing.slice(0, 4).map((code) => (
            <li key={code}>{readinessGateMeta(code).label}</li>
          ))}
        </ul>
        {prerequisiteHint.missing.length > 4 ? (
          <p className="text-muted-foreground mt-1 text-[10px]">
            +{prerequisiteHint.missing.length - 4} more
          </p>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

function NavBottomItem({ to, label, icon, prerequisiteHint }: NavEntry) {
  return (
    <NavLink to={to} end className="contents">
      {({ isActive }) => (
        <MobileBottomNavItem
          icon={
            prerequisiteHint ? (
              <span className="relative inline-flex">
                {icon}
                <AlertTriangle
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 size-2.5 fill-amber-500 text-amber-700 dark:text-amber-300"
                />
              </span>
            ) : (
              icon
            )
          }
          label={label}
          active={isActive}
        />
      )}
    </NavLink>
  );
}

function NavBottomSheetItem({ to, label, icon, prerequisiteHint }: NavEntry) {
  return (
    <NavLink to={to} end className="contents">
      {({ isActive }) => (
        <MobileBottomNavSheetClose asChild>
          <MobileBottomNavSheetItem
            icon={
              prerequisiteHint ? (
                <span className="relative inline-flex">
                  {icon}
                  <AlertTriangle
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 size-2.5 fill-amber-500 text-amber-700 dark:text-amber-300"
                  />
                </span>
              ) : (
                icon
              )
            }
            label={label}
            active={isActive}
          />
        </MobileBottomNavSheetClose>
      )}
    </NavLink>
  );
}

/**
 * Convert a module catalog descriptor to a NavEntry. `not_implemented`
 * modules still render their nav row so the principal can navigate to
 * the "Coming soon" page; the dashboard's module-status grid is the
 * surface that communicates planned-phase information.
 */
function moduleToNavEntry(mod: ModuleDescriptor, profile: VerticalProfile | null): NavEntry {
  const Icon = mod.icon;
  return {
    to: mod.route,
    // Phase SC-3c — the roster page has said "Learners" for a study
    // centre since F0 while the sidebar row leading to it still said
    // "People". Labels now come from the same profile the page does.
    label: profile ? moduleNavLabelFor(profile, mod.key, mod.label) : mod.label,
    icon: <Icon />,
  };
}

export function AppShell() {
  const { user, logout, firstRunComplete } = useAuth();
  const { layout } = useAppearance();
  const navigate = useNavigate();
  const { isModuleEnabled, vertical } = useEnabledModules();
  // Phase H4 FE-2 — cross-module readiness drives the disabled
  // tooltip on Timetable / Attendance nav entries when the
  // tenant catalogue isn't fully configured. The hook is safe to
  // call even when the user is unauthenticated (RequireAuth
  // gates this whole subtree) and degrades to "render normally"
  // on errors so a flaky backend doesn't break nav.
  const readiness = useCatalogueReadiness();
  // Phase H0 hotfix: surface the tenant's logo in the sidebar brand
  // instead of the hard-coded EduViaOS wordmark. The provider sits
  // above the AppShell in App.tsx so this hook can never throw.
  const { branding } = useTenantBrandingContext();
  // Phase F-finale-5 Slice 5a — gate the tenant-self Forms nav entry.
  const { canEditForms } = useCanEditForms();
  // Phase F-finale-6 Slice 6b — gate the tenant-self Admission-funnel entry.
  const { canEditAdmissions } = useCanEditAdmissions();
  // Phase F-finale-7 Slice 7b.1 — gate the tenant-self Seat-assignment
  // entry: `manage_zones` RBAC AND a study-centre tenant.
  const { canEditSeating } = useCanEditSeating();
  // Slice 7b.4d — the booking-admin surface keys off `manage_bookings` (the
  // BE booking guard), NOT `manage_zones`, so a booking-capable admin who
  // lacks zone authoring still sees Bookings and the list query doesn't 403.
  const { canManageBookings } = useCanManageBookings();

  // Phase C1a — gate the entire dashboard surface behind the first-run
  // wizard. `false` (Boolean coercion narrows out null/super-admin)
  // forces a one-way trip to the welcome step; admins land back here
  // automatically once they click "Get started".
  if (firstRunComplete === false) {
    return <Navigate to="/first-run/welcome" replace />;
  }

  // Phase SC-3c — the shell's own copy is vertical-aware from here on.
  // `vertical` is undefined until the dashboard summary resolves; the
  // module rows don't render during that window anyway, and the topbar
  // falls back to a neutral placeholder rather than flashing school
  // vocabulary at a study-centre owner.
  const profile: VerticalProfile | null = vertical ? getVerticalProfile(vertical) : null;
  const showAcademicCatalogue = profile ? hasAcademicCatalogue(profile) : true;
  const showAdmissionAssessments = profile ? hasAdmissionAssessments(profile) : true;

  const moduleNav: NavEntry[] = [];
  for (const m of navModules(MODULE_CATALOG, isModuleEnabled)) {
    const entry = moduleToNavEntry(m, profile);
    // Phase H4 FE-2 — decorate Timetable / Attendance / Substitution
    // entries with their readiness state when the BE call succeeded.
    // Errors (404 NO_BRANCH, 5xx) intentionally fall through to the
    // un-decorated entry so a broken readiness endpoint never blocks
    // the rest of nav.
    // Phase SC-3c — every readiness gate is phrased in catalogue terms
    // ("No sections configured", "No class teachers assigned"), so a
    // study centre would get a warning triangle pointing at concepts it
    // doesn't have and can't create. Suppress the decoration where the
    // catalogue doesn't apply; the route-level PrerequisiteGate still
    // protects the page itself.
    if (readiness.data && showAcademicCatalogue) {
      if (m.key === "timetable" && !readiness.data.timetable.ready) {
        moduleNav.push({
          ...entry,
          prerequisiteHint: {
            missing: readiness.data.timetable.missing,
            counts: readiness.data.timetable.counts,
          },
        });
        continue;
      }
      if (m.key === "attendance" && !readiness.data.attendance.ready) {
        moduleNav.push({
          ...entry,
          prerequisiteHint: {
            missing: readiness.data.attendance.missing,
            counts: readiness.data.attendance.counts,
          },
        });
        continue;
      }
    }
    moduleNav.push(entry);

    if (m.key === "people" && profile?.vertical === "study_centre") {
      moduleNav.push({
        to: "/team",
        label: profile.staffNoun.many,
        icon: <UserCheck />,
      });
    }

    // Phase H5 FE-1 — Assessments is a sibling workspace of
    // Admissions (assessment templates are bound to admission-stage
    // requirements via `template_slug`). We surface it as its own
    // sidebar entry directly below Admissions so the SA can hop into
    // template authoring without traversing a settings sub-page.
    // Gated on `admissions` being enabled so coaching-only tenants
    // who haven't turned admissions on don't see an orphan link.
    // Phase SC-4e — study centres hide this: learner self-tests live
    // in the portal Learn tab; entrance-test authoring is school-shaped.
    if (m.key === "admissions" && showAdmissionAssessments) {
      moduleNav.push({
        to: "/assessments",
        label: "Assessments",
        icon: <ClipboardCheck />,
      });
    }
  }

  const primaryNav: NavEntry[] = [...FIXED_NAV, ...moduleNav];
  const mobilePrimary = primaryNav.slice(0, 4);

  // Phase H6d — splice in the import-students nav entry when the
  // current user holds the new permission. Insert after Catalogue so
  // the data-import surface sits adjacent to the tenant-config block
  // rather than at the bottom next to Security (admins look for it
  // alongside Catalogue, which is also "fill in the institute" work).
  // Phase H8 — the splice logic was extracted to `appShellNav.ts` so
  // it can be unit-tested in isolation; runtime layout is unchanged.
  // Phase F-finale-5 Slice 5a — layer the tenant-self Forms entry on top
  // of the H6d import splice. Both insert after Catalogue, so Forms lands
  // immediately after Catalogue with Import students following it.
  // Phase F-finale-7 — the seat-management surfaces (config + zones) share
  // one gate: `manage_zones` AND a study-centre tenant. Splicing Zones just
  // inside the config splice makes both land after Catalogue with "Seat
  // assignment" first and "Zones" immediately after it.
  const canManageSeating = canEditSeating && vertical === "study_centre";
  // Slice 7b.4d — Bookings clusters with the seat surfaces but gates on
  // `manage_bookings` (not `manage_zones`), still study-centre-only.
  const canManageBookingsHere = canManageBookings && vertical === "study_centre";
  const importPeopleEntry: NavEntry = profile
    ? { ...IMPORT_STUDENTS_NAV_ENTRY, label: importPeopleLabelFor(profile) }
    : IMPORT_STUDENTS_NAV_ENTRY;
  const settingsNav: NavEntry[] = applySeatConfigNavEntry(
    canManageSeating,
    applySeatZonesNavEntry(
      canManageSeating,
      applySeatSeatsNavEntry(
        canManageSeating,
        applySeatAllocationsNavEntry(
          canManageSeating,
          applySeatBookingsNavEntry(
            canManageBookingsHere,
            applySeatFloorNavEntries(
              { canManageSeating, canManageBookingsHere },
              applyAdmissionsEditorNavEntry(
                canEditAdmissions,
                applyFormsNavEntry(
                  canEditForms,
                  applyImportStudentsNavEntry(user, SETTINGS_NAV, importPeopleEntry),
                  FORMS_NAV_ENTRY,
                ),
                ADMISSIONS_EDITOR_NAV_ENTRY,
              ),
              SEAT_FLOOR_NAV_ENTRIES,
            ),
            SEAT_BOOKINGS_NAV_ENTRY,
          ),
          SEAT_ALLOCATIONS_NAV_ENTRY,
        ),
        SEAT_SEATS_NAV_ENTRY,
      ),
      SEAT_ZONES_NAV_ENTRY,
    ),
    SEAT_CONFIG_NAV_ENTRY,
  );

  // Phase SC-3c — Catalogue is subjects + sections + the coverage
  // matrix; a study centre has none of the three, so the row leads to a
  // page it can only be confused by. Dropped *after* the splices rather
  // than before them because every splice anchors to the first settings
  // row — pruning Catalogue up front would re-anchor the whole cluster
  // behind Settings.
  const visibleSettingsNav = showAcademicCatalogue
    ? settingsNav
    : settingsNav.filter((entry) => entry.to !== "/catalogue");
  // Seats are daily operations for a study centre, not setup, so they get
  // their own group instead of sitting under "Other" beside Security.
  const seatingNav = visibleSettingsNav.filter((entry) => entry.to.startsWith("/seating/"));
  const setupNav = visibleSettingsNav.filter((entry) => !entry.to.startsWith("/seating/"));

  const mobileOverflow: NavEntry[] = [...primaryNav.slice(4), ...visibleSettingsNav];

  const onSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials =
    (user?.email ?? "??")
      .split("@")[0]!
      .split(/[._-]/)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("")
      .slice(0, 2) || "?";

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex min-h-svh">
        <Sidebar>
          <SidebarHeader>
            <SidebarBrand
              icon={
                branding?.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt=""
                    className="size-4 rounded-sm object-contain"
                  />
                ) : (
                  <EduViaOSMark className="size-6" />
                )
              }
              tile={branding?.logo_url ? "primary" : "neutral"}
              title="EduViaOS"
              subtitle="Institution Admin"
            />
          </SidebarHeader>
          <SidebarBody>
            <SidebarGroup label="General">
              {primaryNav.map((entry) => (
                <NavSidebarItem key={entry.to} {...entry} />
              ))}
            </SidebarGroup>
            {seatingNav.length > 0 && (
              <SidebarGroup label="Seating">
                {seatingNav.map((entry) => (
                  <NavSidebarItem key={entry.to} {...entry} />
                ))}
              </SidebarGroup>
            )}
            <SidebarGroup label="Setup">
              {setupNav.map((entry) => (
                <NavSidebarItem key={entry.to} {...entry} />
              ))}
            </SidebarGroup>
          </SidebarBody>
          <SidebarFooter>
            <SidebarUser
              name={user?.full_name ?? user?.email?.split("@")[0] ?? "User"}
              email={user?.email ?? ""}
              initials={initials}
              menuTrigger={
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Account menu">
                      <MoreHorizontal aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" side="top">
                    <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onSignOut}>
                      <LogOut className="size-4" aria-hidden="true" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              }
            />
          </SidebarFooter>
        </Sidebar>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar>
            <span className="hidden md:contents">
              <SidebarToggle />
            </span>
            <TopbarSearch placeholder={profile?.chrome.searchPlaceholder ?? "Search…"} />
            <TopbarSpacer />
            <TopbarActions>
              <ThemeCustomizer />
            </TopbarActions>
          </Topbar>

          <main
            className={cn(
              "flex-1 px-4 py-8 sm:px-6 lg:px-8",
              "pb-[calc(80px+env(safe-area-inset-bottom))] md:pb-8",
              layout === "centered" ? "mx-auto w-full max-w-6xl" : "w-full",
            )}
          >
            <Outlet />
          </main>
        </div>

        <MobileBottomNav>
          {mobilePrimary.map((entry) => (
            <NavBottomItem key={entry.to} {...entry} />
          ))}
          <MobileBottomNavMore
            sheetTitle="Navigation"
            sheetDescription="Tap an item to navigate"
            header={
              <div className="border-border bg-muted/40 flex items-center gap-2.5 rounded-lg border px-3 py-2.5">
                {branding?.logo_url ? (
                  <span className="bg-primary text-primary-foreground grid size-8 shrink-0 place-items-center rounded-md">
                    <img
                      src={branding.logo_url}
                      alt=""
                      className="size-4 rounded-sm object-contain"
                    />
                  </span>
                ) : (
                  <span className="bg-card border-border/60 grid size-8 shrink-0 place-items-center rounded-md border">
                    <EduViaOSMark className="size-6" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold leading-tight">EduViaOS</p>
                  <p className="text-muted-foreground truncate text-[11px] leading-tight">
                    Institution Admin
                  </p>
                </div>
              </div>
            }
            footer={
              <div className="flex items-center gap-2.5">
                <div
                  aria-hidden="true"
                  className="bg-primary/15 text-primary grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold"
                >
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium leading-tight">
                    {user?.full_name ?? user?.email?.split("@")[0] ?? "User"}
                  </p>
                  <p className="text-muted-foreground truncate text-xs leading-tight">
                    {user?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSignOut}
                  aria-label="Sign out"
                  className="shrink-0"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                  Sign out
                </Button>
              </div>
            }
          >
            {mobileOverflow.map((entry) => (
              <NavBottomSheetItem key={entry.to} {...entry} />
            ))}
          </MobileBottomNavMore>
        </MobileBottomNav>
      </div>
    </TooltipProvider>
  );
}
